import React, { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Check, ChevronDown, Search } from 'lucide-react';

/**
 * Shared controls for the Appearance page.
 *
 * The vocabulary here is deliberate and worth keeping consistent: anything
 * enumerable is a visible card grid rather than a <select>, colors carry a
 * recents strip, sliders show their live value in the label, and toggles are
 * full-width rows rather than bare checkboxes. Mixing these with ad-hoc form
 * controls mid-page is the thing that made the old panel read as an admin tool
 * instead of a product surface.
 */

/* ------------------------------------------------------------------ *
 * Card + section chrome
 * ------------------------------------------------------------------ */

/** Card header: 36x36 icon chip, title, one line of description. */
export function Section({ icon: Icon, title, subtitle, children, className = '' }) {
  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white dark:border-white/[0.08] dark:bg-[#111] ${className}`}
    >
      <header className="flex items-start gap-3 border-b border-gray-100 px-5 py-4 dark:border-white/[0.06]">
        {Icon && (
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-50 text-gray-500 dark:bg-white/[0.04] dark:text-gray-400">
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight text-gray-900 dark:text-white">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-[13px] leading-snug text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
      </header>
      <div className="space-y-5 px-5 py-5">{children}</div>
    </section>
  );
}

export function Field({ label, hint, error, children, counter }) {
  return (
    <div>
      {(label || counter) && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          {label && (
            <label className="text-[13px] font-medium text-gray-700 dark:text-gray-300">
              {label}
            </label>
          )}
          {counter}
        </div>
      )}
      {children}
      {error ? (
        <p className="mt-1.5 text-[12px] text-red-600 dark:text-red-400">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-[12px] text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  );
}

/** Monospace so the digits stop jittering as they change; amber near the cap. */
export function Counter({ value, max }) {
  const near = value > max * 0.85;
  return (
    <span
      className={`font-mono text-[11px] tabular-nums ${
        near ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'
      }`}
    >
      {value}/{max}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Inputs
 * ------------------------------------------------------------------ */

const inputBase =
  'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[14px] text-gray-900 ' +
  'placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-0 ' +
  'dark:border-white/[0.1] dark:bg-white/[0.03] dark:text-white dark:placeholder:text-gray-500 dark:focus:border-white/40';

export function TextInput({ error, ...props }) {
  return (
    <input
      type="text"
      {...props}
      className={`${inputBase} ${error ? 'border-red-400 dark:border-red-500/60' : ''}`}
    />
  );
}

export function TextArea({ error, ...props }) {
  return (
    <textarea
      {...props}
      className={`${inputBase} resize-none ${error ? 'border-red-400 dark:border-red-500/60' : ''}`}
    />
  );
}

/**
 * Card grid for enumerable choices. `options` is [{ value, label, description,
 * preview }] — preview renders above the label, which is what lets the merchant
 * see a button icon or a theme rather than read its name.
 */
export function SelectionCards({ options, value, onChange, columns = 2 }) {
  return (
    <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`relative rounded-xl border px-3 py-3 text-left transition ${
              active
                ? 'border-gray-900 bg-gray-50 dark:border-white/60 dark:bg-white/[0.06]'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-transparent dark:hover:border-white/20'
            }`}
          >
            {active && (
              <Check className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-gray-900 dark:text-white" />
            )}
            {opt.preview && <div className="mb-2">{opt.preview}</div>}
            <div className="text-[13px] font-medium text-gray-900 dark:text-white">{opt.label}</div>
            {opt.description && (
              <div className="mt-0.5 text-[11.5px] leading-snug text-gray-500 dark:text-gray-400">
                {opt.description}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** iOS-style settings row: the whole row reads as one unit. */
export function ToggleRow({ label, description, checked, onChange, disabled, badge }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-[14px] bg-gray-50 px-4 py-3 dark:bg-white/[0.03] ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium text-gray-900 dark:text-white">{label}</span>
          {badge}
        </div>
        {description && (
          <p className="mt-0.5 text-[12px] leading-snug text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        // p-0 and the explicit left below matter: a button carries default UA
        // padding, and an absolutely positioned child with no `left` falls back
        // to its static position INSIDE that padding. The knob then started ~6px
        // in and its travel pushed it out past the right edge of the track.
        className={`relative h-[26px] w-[46px] flex-shrink-0 rounded-full border-0 p-0 transition-colors ${
          checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-white/20'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-[20px]' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

/** Slider with the live value in the label and preset chips beside it. */
export function SliderField({ label, value, min, max, onChange, presets, suffix = 'px' }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <label className="text-[13px] font-medium text-gray-700 dark:text-gray-300">
          {label}: <span className="font-mono tabular-nums">{value}{suffix}</span>
        </label>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-gray-900 dark:bg-white/15 dark:accent-white"
        />
        {presets && (
          <div className="flex gap-1.5">
            {presets.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => onChange(p.value)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                  value === p.value
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-black'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-gray-300 dark:hover:bg-white/[0.12]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Searchable dropdown
 * ------------------------------------------------------------------ */

/**
 * Replaces a native <select>. With 46 fonts the native list rendered as a
 * full-height OS menu that covered the whole page and the preview with it, and
 * gave no way to search — you scrolled hunting for a name you already knew.
 *
 * Options are [{ value, label }]. `renderOption` styles each row (the font
 * picker previews each face in itself).
 */
export function SearchSelect({ value, options, onChange, placeholder = 'Search…', renderOption }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      // Start on the current value so Enter without typing is a no-op rather
      // than silently selecting whatever happens to be first.
      setActive(Math.max(0, options.findIndex((o) => o.value === value)));
      inputRef.current?.focus();
    }
  }, [open, options, value]);

  // Keep the highlighted row in view while arrowing through 46 entries.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const commit = (option) => {
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(filtered[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${inputBase} flex items-center justify-between text-left`}
      >
        <span className="truncate">{selected ? selected.label : 'Choose…'}</span>
        <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/[0.1] dark:bg-[#181818]">
          <div className="border-b border-gray-100 p-2 dark:border-white/[0.06]">
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-white/[0.04]">
              <Search className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onKeyDown}
                className="w-full bg-transparent text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-white"
              />
            </div>
          </div>

          <div ref={listRef} className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-[12.5px] text-gray-500 dark:text-gray-400">
                Nothing matches &ldquo;{query}&rdquo;.
              </p>
            )}
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => commit(o)}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-[13.5px] transition ${
                  i === active
                    ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="truncate">{renderOption ? renderOption(o) : o.label}</span>
                {o.value === value && (
                  <Check className="ml-2 h-3.5 w-3.5 flex-shrink-0 text-gray-900 dark:text-white" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Color picker
 * ------------------------------------------------------------------ */

const RECENTS_KEY = 'pb_recent_colors';
const MAX_RECENT = 5;
/** Seeded so the strip is never empty on a fresh account. */
const SEED_RECENTS = ['#000000', '#FFFFFF', '#667EEA'];

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.length) return parsed.slice(0, MAX_RECENT);
  } catch {
    /* fall through to the seed */
  }
  return SEED_RECENTS;
}

function pushRecent(color) {
  const next = [color, ...readRecents().filter((c) => c.toLowerCase() !== color.toLowerCase())].slice(
    0,
    MAX_RECENT,
  );
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* private mode — the strip just resets to the seed next load */
  }
  return next;
}

export function ColorField({ label, value, onChange, hint, error, allowEmpty, emptyLabel }) {
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState(readRecents);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (color) => {
    onChange(color);
    setRecents(pushRecent(color));
  };

  return (
    <Field label={label} hint={hint} error={error}>
      <div className="relative" ref={ref}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 dark:border-white/[0.12]"
            style={{ background: value || 'transparent' }}
            aria-label={`${label} swatch`}
          >
            {!value && <span className="text-[10px] text-gray-400">auto</span>}
          </button>
          <input
            type="text"
            value={value || ''}
            placeholder={allowEmpty ? emptyLabel || 'Auto' : '#2E5BFF'}
            onChange={(e) => {
              const next = e.target.value.trim();
              onChange(next === '' && allowEmpty ? '' : next);
            }}
            className={`${inputBase} font-mono uppercase ${error ? 'border-red-400 dark:border-red-500/60' : ''}`}
          />
        </div>

        <div className="mt-2 flex items-center gap-1.5">
          {recents.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => commit(c)}
              title={c}
              className="h-6 w-6 rounded-md border border-gray-200 dark:border-white/[0.12]"
              style={{ background: c }}
            />
          ))}
          {allowEmpty && value !== '' && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="ml-1 text-[11px] text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
            >
              Reset to auto
            </button>
          )}
        </div>

        {open && (
          <div className="absolute z-30 mt-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl dark:border-white/[0.1] dark:bg-[#181818]">
            <HexColorPicker color={value || '#2E5BFF'} onChange={onChange} />
            <button
              type="button"
              onClick={() => {
                if (value) commit(value);
                setOpen(false);
              }}
              className="mt-3 w-full rounded-lg bg-gray-900 py-2 text-[13px] font-medium text-white dark:bg-white dark:text-black"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Field>
  );
}
